import { useRef, useEffect } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import clsx from 'clsx';
import { useOptionalBookmarkSelection } from '../../../contexts/BookmarkSelectionContext';
import { useOptionalBookmarkExitAnimation } from '../../../contexts/BookmarkExitAnimationContext';
import {
  useOptionalBookmarkDnd,
  type BookmarkDragData,
  type DropIndicatorPosition,
} from '../../../contexts/BookmarkDndContext';
import { isMobileDevice } from '../../../utils/platformUtils';
import { sortableBookmarkId } from '../../../services/bookmarkReorder';
import BookmarkRow from './BookmarkRow';
import type { Bookmark, BookmarkActionViewMode } from '../../../types/api';

interface DraggableBookmarkRowProps extends Bookmark {
  condensedView?: boolean;
  showThumbnails?: boolean;
  viewMode?: BookmarkActionViewMode;
  currentFolderId?: string;
  profileMode?: boolean;
  showLocation?: boolean;
  sortingEnabled?: boolean;
}

function DropIndicator({ position }: { position: DropIndicatorPosition }) {
  if (!position) return null;
  return (
    <div
      className={clsx(
        'sort-indicator',
        position === 'above' ? '-top-[2.5px]' : '-bottom-[2.5px]',
      )}
    />
  );
}

function DraggableBookmarkRow(props: DraggableBookmarkRowProps) {
  const { id, title, url, sortingEnabled = false, ...restProps } = props;
  const selectionContext = useOptionalBookmarkSelection();
  const exitAnimation = useOptionalBookmarkExitAnimation();
  const dndContext = useOptionalBookmarkDnd();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const isExiting = exitAnimation?.exitingBookmarkIds.has(id) ?? false;

  useEffect(() => {
    if (!isExiting || !wrapperRef.current) return;

    const element = wrapperRef.current;
    const handleTransitionEnd = (e: TransitionEvent) => {
      // only respond to our own opacity transition
      if (e.target === element && e.propertyName === 'opacity') {
        exitAnimation?.onExitComplete(id);
      }
    };

    element.addEventListener('transitionend', handleTransitionEnd);
    return () => element.removeEventListener('transitionend', handleTransitionEnd);
  }, [isExiting, id, exitAnimation]);

  const selectedIds = selectionContext?.selectedBookmarkIds ?? new Set<string>();
  const isSelected = selectedIds.has(String(id));
  const bookmarkIds = isSelected ? Array.from(selectedIds) : [id];

  const sortableId = sortableBookmarkId(id);
  const dragData: BookmarkDragData = {
    type: 'bookmark',
    bookmarkIds,
    singleBookmarkId: id,
    bookmarkTitle: title,
    sortable: sortingEnabled,
  };

  const isDragDisabled = isMobileDevice();

  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    id: sortableId,
    data: dragData,
    disabled: isDragDisabled,
  });

  const isBeingDragged =
    isDragging || (dndContext?.isDragging && dndContext.draggedBookmarkIds.includes(id));

  const dropIndicatorPosition = sortingEnabled
    ? (dndContext?.getDropIndicatorPosition(sortableId) ?? null)
    : null;
  const justMoved = dndContext?.justMovedId === sortableId;
  const suppressTransition =
    dndContext?.justMovedId != null && dndContext.justMovedId !== sortableId;

  // Indicator-only reorder: keep rows in place (gray the source) and show
  // drop borders. Do not apply dnd-kit sibling transforms — they shift cells
  // into the still-visible source and cause overlap.
  const style = (() => {
    if (isDragging || isBeingDragged) return { opacity: 0.4 };
    if (!sortingEnabled) return undefined;
    if (suppressTransition) return undefined;
    if (justMoved) {
      return { transform: 'scale(0.98)', transition: 'transform 150ms ease-out' };
    }
    return undefined;
  })();

  const combinedRef = (node: HTMLDivElement | null) => {
    setNodeRef(node);
    (wrapperRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
  };

  // dnd-kit's sortable attributes include role="button",
  // aria-pressed, and aria-roledescription="sortable",
  // which makes this wrapper an interactive control containing the
  // (interactive) BookmarkRow — a nested-interactive violation. Drop those
  // attrs so the row inside is the real interactive surface, and
  // aria-describedby still routes SR users to dnd-kit's instructions.
  const {
    role: _ignoredRole,
    tabIndex: _ignoredTabIndex,
    'aria-pressed': _ignoredAriaPressed,
    'aria-roledescription': _ignoredAriaRoleDescription,
    ...dragAttrs
  } = attributes;

  return (
    <div
      ref={combinedRef}
      data-bookmark-id={id}
      data-drop-indicator={dropIndicatorPosition ?? undefined}
      style={style}
      className={clsx(
        'bookmark-drag-wrapper relative overflow-hidden transition-all duration-200 outline-none',
        isExiting && 'max-h-0 opacity-0',
        !isExiting && 'max-h-[200px]',
        isBeingDragged && 'opacity-40',
        !isDragDisabled && 'cursor-grab',
        isDragging && 'cursor-grabbing',
      )}
      {...dragAttrs}
      {...listeners}
      tabIndex={-1}
    >
      <DropIndicator position={dropIndicatorPosition} />
      <BookmarkRow id={id} title={title} url={url} {...restProps} />
    </div>
  );
}

export default DraggableBookmarkRow;
